import React from 'react';
import { Search, Compass, Code, Rocket, ArrowRight } from 'lucide-react';

export const ProcessSection: React.FC = () => {
  const steps = [
    {
      number: '01',
      title: 'Discover',
      description: 'Understand business goals, target users, technical requirements, and core challenges.',
      icon: Search,
      color: 'border-[#159DE8] text-[#159DE8]'
    },
    {
      number: '02',
      title: 'Design',
      description: 'Create intuitive user experiences, architectural specifications, and interactive wireframes.',
      icon: Compass,
      color: 'border-[#7B2EFF] text-[#7B2EFF]'
    },
    {
      number: '03',
      title: 'Develop',
      description: 'Build, integrate, and test clean, type-safe code using iterative sprint methodologies.',
      icon: Code,
      color: 'border-[#FF851B] text-[#FF851B]'
    },
    {
      number: '04',
      title: 'Launch & Grow',
      description: 'Deploy to high-availability cloud edge, monitor performance, and provide continuous SLA support.',
      icon: Rocket,
      color: 'border-[#00C853] text-[#00C853]'
    }
  ];

  return (
    <section className="bg-[#071426] py-20 border-b border-[#1b2f4a] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Heading */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-3">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-8 bg-[#FF851B]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B]">
              HOW WE WORK
            </span>
            <span className="h-px w-8 bg-[#FF851B]" />
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight font-display">
            Our Working Process
          </h2>

          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            A disciplined, agile delivery framework engineered to minimize risk and accelerate time-to-market.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connecting line on desktop */}
          <div className="hidden lg:block absolute top-1/2 left-16 right-16 h-0.5 bg-gradient-to-r from-[#159DE8] via-[#7B2EFF] via-[#FF851B] to-[#00C853] -translate-y-8 z-0 opacity-40" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-[#0c1a2d] border border-[#1b2f4a] hover:border-[#159DE8]/50 transition-all duration-300 relative group flex flex-col justify-between"
                >
                  <div>
                    {/* Top row with step number badge and icon */}
                    <div className="flex items-center justify-between mb-5">
                      <div className="w-12 h-12 rounded-2xl bg-[#050A13] border border-[#1b2f4a] flex items-center justify-center font-display font-extrabold text-lg text-white group-hover:scale-110 transition-transform shadow-inner">
                        <span className="text-gradient-orange">{step.number}</span>
                      </div>
                      
                      <div className={`p-2.5 rounded-xl bg-[#0B2342] border ${step.color} shrink-0`}>
                        <Icon size={20} />
                      </div>
                    </div>

                    {/* Step Title */}
                    <h3 className="text-lg font-bold text-white mb-2 font-display group-hover:text-[#FF851B] transition-colors">
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-[#8FA0B8] leading-relaxed group-hover:text-[#D5DFEC] transition-colors">
                      {step.description}
                    </p>
                  </div>

                  <div className="pt-4 mt-4 border-t border-[#1b2f4a]/40 flex items-center text-[11px] font-medium text-[#8FA0B8]">
                    <span>Stage {idx + 1} of 4</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};
